import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Trophy, Share2, Wallet, Gift, ChevronRight, Sparkles, Users, Ticket } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Seo from '@/components/Seo';
import { API_URL } from '@/config/api';

const fadeUp = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

export default function AppRewardsGuide() {
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/rewards/program`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setProgram)
      .catch(e => {
        console.warn('Rewards program fetch failed:', e);
        setLoadFailed(true);
      });
    window.scrollTo(0, 0);
  }, []);

  const tiers = program?.tiers || [];
  const r = program?.referral || { referrer_reward: 15, referee_reward: 0, min_spend: 70, milestone_threshold: 3, milestone_reward: 70 };
  const t1 = tiers[0] || { name: 'Bronze', emoji: '🥉', orders_needed: 10, discount_percent: 10 };
  const sample = 144; // two R72 mains
  const milestoneTotal = r.referrer_reward * r.milestone_threshold + r.milestone_reward;

  const downloadPdf = () => {
    window.open(`${API_URL}/api/downloads/rewards-guide`, '_blank');
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/rewards-guide`;
    const msg = `🍽️ Check out the GC3H Canteen rewards programme — loyalty discounts on your meals, R${r.referrer_reward} for every friend you invite, and free lunches! See how it works: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="rewards-guide-page">
      <Seo
        title="Rewards & Loyalty Guide"
        path="/rewards-guide"
        description={`How GC3H rewards work: 10 loyalty tiers with up to 35% off, R${r.referrer_reward} per referral, free-lunch milestones, wallet perks and gift vouchers — explained with examples.`}
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-amber-400 to-primary px-5 pt-6 pb-8 rounded-b-[2rem]">
        <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center text-white mb-4" data-testid="rewards-guide-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-white/85 text-xs font-semibold uppercase tracking-wider mb-1">GC3H Online Canteen</p>
            <h1 className="text-2xl font-bold text-white leading-tight">Rewards &amp; Loyalty <br />Programme Guide</h1>
            <p className="text-white/90 text-sm mt-2 max-w-md">Every meal counts. Here's exactly how you earn discounts, free lunches and wallet credit — with real Rand examples.</p>
          </div>
          <Trophy className="h-12 w-12 text-white/70 shrink-0" />
        </div>
        <div className="mt-4 flex gap-2.5">
          <button
            onClick={downloadPdf}
            className="h-11 px-4 bg-white text-amber-700 rounded-xl font-semibold text-sm flex items-center gap-2 shadow-md active:scale-95 transition-transform"
            data-testid="rewards-guide-download-pdf"
          >
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button
            onClick={shareWhatsApp}
            className="h-11 px-4 bg-emerald-500 text-white rounded-xl font-semibold text-sm flex items-center gap-2 shadow-md active:scale-95 transition-transform"
            data-testid="rewards-guide-share-whatsapp"
          >
            <Share2 className="h-4 w-4" /> Share on WhatsApp
          </button>
        </div>
      </div>

      <div className="px-5 -mt-3 space-y-4 pb-8">
        {/* Three ways to earn */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-5" data-testid="rewards-three-ways">
          <h2 className="text-base font-bold text-secondary-900 dark:text-white mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> Three ways to earn</h2>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0"><Trophy className="h-4.5 w-4.5 text-amber-600" /></div>
              <p className="text-sm text-secondary-700 dark:text-slate-300"><strong>Loyalty tiers</strong> — every paid daily meal counts toward your lifetime total. Each tier you reach unlocks an automatic discount (top tiers add free items).</p>
            </div>
            <div className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0"><Share2 className="h-4.5 w-4.5 text-violet-600" /></div>
              <p className="text-sm text-secondary-700 dark:text-slate-300"><strong>Referrals</strong> — earn <strong>R{r.referrer_reward}</strong> wallet credit for every friend whose first order is <strong>R{r.min_spend}+</strong>, plus a <strong>R{r.milestone_reward}</strong> free-lunch voucher for every <strong>{r.milestone_threshold}</strong> qualifying referrals.</p>
            </div>
            <div className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0"><Wallet className="h-4.5 w-4.5 text-emerald-600" /></div>
              <p className="text-sm text-secondary-700 dark:text-slate-300"><strong>Wallet &amp; vouchers</strong> — top up for instant checkout, redeem gift vouchers into credit, and gift themed vouchers to colleagues.</p>
            </div>
          </div>
        </motion.div>

        {/* Tier ladder */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden" data-testid="rewards-tier-ladder">
          <div className="bg-secondary-900 dark:bg-slate-900 p-5">
            <h2 className="text-base font-bold text-white flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" /> The 10 loyalty tiers</h2>
            <p className="text-white/70 text-xs mt-1">Paid daily meals move you up. Rewards get bigger the longer you stay.</p>
          </div>
          <div className="divide-y divide-border dark:divide-slate-700">
            {tiers.map((t) => (
              <div key={t.tier} className="flex items-center gap-3 px-5 py-3" data-testid={`rewards-tier-row-${t.tier}`}>
                <span className="text-xl w-8 text-center shrink-0">{t.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-secondary-900 dark:text-white">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.orders_needed} lifetime meals</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-primary">{t.discount_percent}% off</p>
                  {t.perk && <p className="text-[10px] font-semibold text-emerald-600">{t.perk}</p>}
                </div>
              </div>
            ))}
            {tiers.length === 0 && (
              <p className="px-5 py-4 text-sm text-muted-foreground" data-testid="rewards-tier-placeholder">
                {loadFailed ? 'Tier details are temporarily unavailable — please try again shortly.' : 'Loading tiers…'}
              </p>
            )}
          </div>
        </motion.div>

        {/* How the discount works */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-5" data-testid="rewards-how-it-works">
          <h2 className="text-base font-bold text-secondary-900 dark:text-white mb-3">How the tier discount works</h2>
          <ol className="space-y-2.5 text-sm text-secondary-700 dark:text-slate-300 list-none">
            <li className="flex gap-2.5"><span className="h-5 w-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>Every <strong>paid daily meal</strong> adds to your lifetime meal count (sides and shop items don't count).</li>
            <li className="flex gap-2.5"><span className="h-5 w-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>When your paid-meal count reaches a tier's target, that tier's reward unlocks — and the discount is applied automatically to your <strong>next order</strong>. No codes needed.</li>
            <li className="flex gap-2.5"><span className="h-5 w-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>The discount applies <strong>once</strong>, to the eligible meal items on that one order. Don't order right away? It stays reserved until you do — you can't lose it.</li>
            <li className="flex gap-2.5"><span className="h-5 w-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>Then you keep ordering toward the next tier — you can <strong>never lose</strong> progress.</li>
          </ol>
          <div className="mt-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-4" data-testid="rewards-tier-example">
            <p className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">Worked example</p>
            <p className="text-sm text-secondary-800 dark:text-slate-200">
              Your 10th paid meal unlocks {t1.emoji} <strong>{t1.name}</strong>. On your <strong>next order</strong> — say two R72 mains (<strong>R{sample}</strong>) — <strong>{t1.discount_percent}% off</strong> applies automatically: you save <strong>R{(sample * t1.discount_percent / 100).toFixed(2)}</strong> and pay <strong>R{(sample * (1 - t1.discount_percent / 100)).toFixed(2)}</strong>.
            </p>
          </div>
        </motion.div>

        {/* Referral programme */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg overflow-hidden" data-testid="rewards-referral-section">
          <div className="bg-gradient-to-r from-violet-600 to-primary p-5 text-white">
            <h2 className="text-base font-bold flex items-center gap-2"><Users className="h-4 w-4" /> The referral programme</h2>
            <p className="text-white/85 text-xs mt-1">Share lunch, earn lunch.</p>
          </div>
          <div className="p-5 space-y-2.5 text-sm text-secondary-700 dark:text-slate-300">
            <p>• Your personal invite code and WhatsApp share button live in <strong>Profile → Invite &amp; earn</strong>.</p>
            <p>• When a friend joins with your link and their <strong>first paid order is R{r.min_spend}+</strong>, <strong>R{r.referrer_reward}</strong> lands in your wallet automatically.{r.referee_reward > 0 && <> Your friend gets <strong>R{r.referee_reward}</strong> too.</>}</p>
            {r.milestone_threshold > 0 && (
              <p>• <strong>Free-lunch milestone:</strong> every <strong>{r.milestone_threshold}</strong> qualifying referrals adds a <strong>R{r.milestone_reward}</strong> voucher to your wallet — repeatable, forever.</p>
            )}
            <p>• Track invites, earnings and your <strong>leaderboard rank</strong> in your profile.</p>
            <div className="rounded-2xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/50 p-4 mt-3" data-testid="rewards-referral-example">
              <p className="text-xs font-bold text-violet-800 dark:text-violet-300 uppercase tracking-wide mb-1">Worked example</p>
              <p className="text-sm text-secondary-800 dark:text-slate-200">
                Invite {r.milestone_threshold || 3} colleagues who each place a qualifying first order → {r.milestone_threshold || 3} × R{r.referrer_reward}{r.milestone_threshold > 0 && <> + a R{r.milestone_reward} free-lunch voucher</>} = <strong>R{milestoneTotal.toFixed(0)} in your wallet</strong>.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Wallet & vouchers */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-5" data-testid="rewards-wallet-section">
          <h2 className="text-base font-bold text-secondary-900 dark:text-white mb-3 flex items-center gap-2"><Ticket className="h-4 w-4 text-emerald-600" /> Wallet &amp; voucher perks</h2>
          <div className="space-y-2.5 text-sm text-secondary-700 dark:text-slate-300">
            <p>• <strong>Top up once, checkout instantly</strong> — pay straight from your wallet, no card each time.</p>
            <p>• <strong>Gift vouchers</strong> — buy themed vouchers (Birthday, Thank You, Congratulations &amp; more) for colleagues; they redeem the code into wallet credit.</p>
            <p>• <strong>All rewards stack in one wallet</strong> — referral credit, milestone vouchers and refunds can pay for any order.</p>
            <p>• <strong>Low-balance reminders</strong> — opt in and we'll nudge you before your balance runs dry.</p>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div {...fadeUp} className="bg-white dark:bg-slate-800 rounded-3xl shadow-lg p-5" data-testid="rewards-faq">
          <h2 className="text-base font-bold text-secondary-900 dark:text-white mb-1">FAQ</h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="q1">
              <AccordionTrigger className="text-sm text-left" data-testid="rewards-faq-q1">Do I need to activate my tier discount?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">No — once you reach a milestone, the discount is applied automatically to your next order. It's used once, then you start earning toward the next tier.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2">
              <AccordionTrigger className="text-sm text-left" data-testid="rewards-faq-q2">Which items count toward my meal total?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">Paid daily meals. Sides, drinks and canteen-shop items don't increase the count, and tier discounts apply to eligible meals only.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3">
              <AccordionTrigger className="text-sm text-left" data-testid="rewards-faq-q3">Can I lose my tier?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">Never. Your lifetime meal count only goes up, and every tier reward is yours once unlocked.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4">
              <AccordionTrigger className="text-sm text-left" data-testid="rewards-faq-q4">When exactly is referral credit paid?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">The moment your friend's first order of R{r.min_spend}+ is paid — you'll get a notification.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="q5">
              <AccordionTrigger className="text-sm text-left" data-testid="rewards-faq-q5">Where do I see my rewards?</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">Home shows your tier progress; Profile shows wallet balance, referral stats and your leaderboard rank.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeUp} className="flex gap-3">
          <button onClick={() => navigate('/menu')} className="flex-1 h-12 bg-primary text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 shadow-md" data-testid="rewards-guide-cta-menu">
            Start earning — order now <ChevronRight className="h-4 w-4" />
          </button>
          <button onClick={shareWhatsApp} className="h-12 px-4 bg-emerald-500 text-white rounded-xl font-semibold text-sm flex items-center gap-1.5" data-testid="rewards-guide-cta-share">
            <Share2 className="h-4 w-4" />
          </button>
          <button onClick={downloadPdf} className="h-12 px-4 bg-secondary-900 dark:bg-slate-700 text-white rounded-xl font-semibold text-sm flex items-center gap-1.5" data-testid="rewards-guide-cta-download">
            <Download className="h-4 w-4" /> PDF
          </button>
        </motion.div>

        <p className="text-[10px] text-muted-foreground text-center px-4">Programme values reflect the current live configuration and may be updated from time to time.</p>
      </div>
    </div>
  );
}
